import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ReportExportFormat, ReportExportLocale } from "@multi-chat/shared";
import { downloadReportExport } from "./report-export-download.js";

export function useReportExport(): {
  exporting: boolean;
  exportError: string | null;
  clearExportError: () => void;
  runExport: (input: {
    pathWithQuery: string;
    fallbackFilename: string;
  }) => Promise<void>;
} {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const runExport = async (input: {
    pathWithQuery: string;
    fallbackFilename: string;
  }) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setExporting(true);
    setExportError(null);
    try {
      await downloadReportExport(input);
    } catch {
      setExportError(t("reports.export.failed"));
    } finally {
      inFlight.current = false;
      setExporting(false);
    }
  };

  return {
    exporting,
    exportError,
    clearExportError: () => setExportError(null),
    runExport,
  };
}

export function exportLocaleFromUi(language: string): ReportExportLocale {
  return language.toLowerCase().startsWith("he") ? "he" : "en";
}

export type { ReportExportFormat };
